import styled from '@emotion/styled'
import { PaperClipOutlined, PlusOutlined } from '@ant-design/icons'
import { Form, Input, Modal, Typography, Upload, message } from 'antd'
import type { UploadProps } from 'antd'
import type { UploadFile } from 'antd/es/upload/interface'
import { FC, useEffect, useState } from 'react'
import { allowFileExtensionsImage } from 'config'
import { renameUploadFile } from 'helpers/utils'
import {
  useCreateContactActivity,
  useGetContactActivity,
  useUpdateContactActivity,
} from 'hooks/useFactoryContactActivity'
import { uploadFile, uploadImage } from 'services/factoryAPI'
import { fileNameFromUrl } from './helpers'

interface ContactNoteFormValues {
  topic?: string
  description?: string
}

interface ContactNoteModalProps {
  open: boolean
  /** มีค่า = โหมดแก้ไข */
  editingId?: string | null
  refId: string
  refType: string
  processType: string
  factoryId: string
  onClose: () => void
}

const buildFileListFromUrls = (urls?: string[] | null): UploadFile[] =>
  (urls ?? []).map((url, index) => ({
    uid: `${index}-${url}`,
    name: fileNameFromUrl(url, index),
    status: 'done',
    url,
  }))

const extractUrls = (fileList: UploadFile[]): string[] =>
  fileList
    .map((f) => (f.response?.url as string) ?? f.url)
    .filter((url): url is string => !!url)

/** เปลี่ยนชื่อไฟล์ให้ปลอดภัย + จำกัดขนาดไม่เกิน 5 MB */
const onBeforeUpload: UploadProps['beforeUpload'] = (file) => {
  const newFile = renameUploadFile(file)

  if (file.size > 1024 * 1024 * 5) {
    message.error('ไฟล์ขนาดใหญ่เกิน 5 MB')
    return Upload.LIST_IGNORE
  }

  return newFile
}

const ContactNoteModal: FC<ContactNoteModalProps> = ({
  open,
  editingId,
  refId,
  refType,
  processType,
  factoryId,
  onClose,
}) => {
  const [form] = Form.useForm<ContactNoteFormValues>()
  const [imageFileList, setImageFileList] = useState<UploadFile[]>([])
  const [attachFileList, setAttachFileList] = useState<UploadFile[]>([])
  const isEdit = !!editingId

  const { data: editingData, isLoading: editingLoading } = useGetContactActivity(
    editingId ?? null,
    open
  )
  const { mutate: createMutate, isPending: creating } = useCreateContactActivity()
  const { mutate: updateMutate, isPending: updating } = useUpdateContactActivity()
  const isSaving = creating || updating

  useEffect(() => {
    if (!open) return
    if (isEdit && editingData?.data) {
      form.setFieldsValue({
        topic: editingData.data.topic ?? undefined,
        description: editingData.data.description ?? undefined,
      })
      setImageFileList(buildFileListFromUrls(editingData.data.image_list))
      setAttachFileList(buildFileListFromUrls(editingData.data.file_list))
    } else if (!isEdit) {
      form.resetFields()
      setImageFileList([])
      setAttachFileList([])
    }
  }, [open, isEdit, editingData, form])

  const handleClose = () => {
    form.resetFields()
    setImageFileList([])
    setAttachFileList([])
    onClose()
  }

  const handleSubmit = async () => {
    const values = await form.validateFields()

    const payload = {
      factory_id: factoryId,
      ref_id: refId,
      ref_type: refType,
      process_type: processType,
      topic: values.topic,
      description: values.description,
      image_list: extractUrls(imageFileList),
      file_list: extractUrls(attachFileList),
    }

    if (isEdit && editingId) {
      updateMutate(
        { id: editingId, ...payload },
        {
          onSuccess: () => {
            message.success('แก้ไขบันทึกการติดต่อสำเร็จ')
            handleClose()
          },
          onError: (error) => {
            message.error(error?.message || 'แก้ไขบันทึกการติดต่อไม่สำเร็จ')
          },
        }
      )
    } else {
      createMutate(payload, {
        onSuccess: () => {
          message.success('เพิ่มบันทึกการติดต่อสำเร็จ')
          handleClose()
        },
        onError: (error) => {
          message.error(error?.message || 'เพิ่มบันทึกการติดต่อไม่สำเร็จ')
        },
      })
    }
  }

  return (
    <Modal
      open={open}
      title={isEdit ? 'แก้ไขบันทึกการติดต่อ' : 'เพิ่มบันทึกการติดต่อ'}
      okText="บันทึก"
      cancelText="ยกเลิก"
      onOk={handleSubmit}
      onCancel={handleClose}
      confirmLoading={isSaving}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        disabled={isSaving || (isEdit && editingLoading)}
      >
        {/* ── ข้อมูลบันทึก: หัวข้อ + รายละเอียด ── */}
        <Form.Item
          name="topic"
          label="หัวข้อ"
          rules={[{ required: true, message: 'กรุณากรอกหัวข้อ' }]}
        >
          <Input placeholder="หัวข้อการติดต่อ" maxLength={200} />
        </Form.Item>
        <Form.Item name="description" label="รายละเอียด">
          <Input.TextArea
            placeholder="รายละเอียดการติดต่อ"
            autoSize={{ minRows: 3, maxRows: 6 }}
            maxLength={2000}
          />
        </Form.Item>

        {/* ── รูปภาพแนบ (สูงสุด 5 รูป) ── */}
        <SectionLabel>รูปภาพแนบ</SectionLabel>
        <Form.Item>
          <Upload
            listType="picture-card"
            fileList={imageFileList}
            accept={allowFileExtensionsImage}
            maxCount={5}
            beforeUpload={onBeforeUpload}
            customRequest={async ({ file, onSuccess, onError }) => {
              try {
                const result = await uploadImage(file as File)
                onSuccess?.(result)
              } catch (e) {
                onError?.(e as Error)
              }
            }}
            onChange={({ fileList: next }) => setImageFileList(next)}
          >
            {imageFileList.length >= 5 ? null : (
              <UploadTrigger>
                <PlusOutlined style={{ fontSize: 20 }} />
                <span>อัปโหลด</span>
              </UploadTrigger>
            )}
          </Upload>
        </Form.Item>

        {/* ── ไฟล์แนบ (สูงสุด 5 ไฟล์) ── */}
        <SectionLabel>ไฟล์แนบ</SectionLabel>
        <Form.Item style={{ marginBottom: 0 }}>
          <Upload
            fileList={attachFileList}
            maxCount={5}
            beforeUpload={onBeforeUpload}
            customRequest={async ({ file, onSuccess, onError }) => {
              try {
                const result = await uploadFile(file as File)
                onSuccess?.(result)
              } catch (e) {
                onError?.(e as Error)
              }
            }}
            onChange={({ fileList: next }) => setAttachFileList(next)}
          >
            {attachFileList.length >= 5 ? null : (
              <AttachButton>
                <PaperClipOutlined /> แนบไฟล์
              </AttachButton>
            )}
          </Upload>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            อัปโหลดรูปและไฟล์ได้อย่างละไม่เกิน 5 รายการ
          </Typography.Text>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default ContactNoteModal

// ─── Styled ──────────────────────────────────────────────────────────────────

const SectionLabel = styled.div`
  font-weight: 500;
  margin-bottom: 8px;
`

const UploadTrigger = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-size: 12px;
`

const AttachButton = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.2s;
  &:hover {
    border-color: #1677ff;
    color: #1677ff;
  }
`
